import { Badge, Box, HStack, Text } from '@chakra-ui/react'
import { formatInTimeZone } from 'date-fns-tz'

function formatShortOffset(timeZone: string, instant: Date): string {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
    }).formatToParts(instant)

    return parts.find(part => part.type === 'timeZoneName')?.value ?? ''
}

type TimezoneCardProps = {
    label: string
    iana: string
    isSelected: boolean
    matchesSystemTimezone: boolean
    now: Date
    onSelect: () => void
}

export function TimezoneCard({ label, iana, isSelected, matchesSystemTimezone, now, onSelect }: TimezoneCardProps) {
    return (
        <Box
            as="button"
            type="button"
            textAlign="left"
            borderRadius="lg"
            borderWidth="2px"
            borderStyle={matchesSystemTimezone ? 'dashed' : 'solid'}
            borderColor={isSelected || matchesSystemTimezone ? 'blue.400' : 'gray.200'}
            bg="white"
            color="gray.800"
            boxShadow="sm"
            p={2}
            cursor="pointer"
            transition="border-color 0.15s ease, box-shadow 0.15s ease"
            _hover={{
                borderColor: isSelected || matchesSystemTimezone ? 'blue.500' : 'gray.300',
                boxShadow: 'md',
            }}
            onClick={onSelect}
            w="100%">
            <HStack justify="space-between" alignItems="flex-end">
                <Text fontSize="sm" fontWeight="bold" fontStyle="italic" color={isSelected ? 'blue.600' : 'gray.600'}>
                    {label}
                </Text>
                <Box>
                    {matchesSystemTimezone && (
                        <Badge borderRadius="full" px={2} bg="green.50" color="green.700" fontSize="xs">
                            LOCAL TIMEZONE
                        </Badge>
                    )}
                    {!matchesSystemTimezone && (
                        <Badge
                            borderRadius="full"
                            px={2}
                            bg={isSelected ? 'blue.50' : 'gray.100'}
                            color={isSelected ? 'blue.700' : 'gray.700'}
                            fontSize="xs">
                            {formatShortOffset(iana, now)}
                        </Badge>
                    )}
                </Box>
            </HStack>
            <Text fontSize="3xl" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatInTimeZone(now, iana, 'HH:mm:ss')}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={2}>
                {formatInTimeZone(now, iana, 'EEEE, MMM d')}
            </Text>
        </Box>
    )
}
